import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { getMonthNames, getWeekdayNames } from '../../common/calendar/utils';
import { useSystemClassifierItemById } from '../../configuration';
import { useSystemClassifiers } from '../../configuration/useSystemClassifiers';
import { SystemClassifierItemDto } from '../../systemClassifier';
import { ExecutionPeriodFormDataType } from '../schema';
import translations from '../translations/formatPeriodicity.translations.json';
import { PredefineClassifiers } from './types';

const { addTranslations, t } = i18nNsFactory('format-periodicity');
addTranslations(translations);

export function formatPeriodicity(
  periodicity: ExecutionPeriodFormDataType,
  predefines: PredefineClassifiers
) {
  let text = t('period.minutes.label');
  if (periodicity.minutesPredefined) {
    text += predefines.minutes.get(periodicity.minutesPredefined)?.name ?? '';
  } else if (periodicity.minutesScheduled?.length) {
    text += periodicity.minutesScheduled.join(', ');
  } else {
    text += t('period.minutes.all');
  }

  text += '; ' + t('period.hours.label');
  if (periodicity.hoursPredefined) {
    text += predefines.hours.get(periodicity.hoursPredefined)?.name ?? '';
  } else if (periodicity.hoursScheduled?.length) {
    text += periodicity.hoursScheduled.join(', ');
  } else {
    text += t('period.hours.all');
  }

  text += '; ' + t('period.days.label');
  if (periodicity.daysPredefined) {
    text += predefines.days.get(periodicity.daysPredefined)?.name ?? '';
  } else if (periodicity.daysScheduled?.length) {
    text += periodicity.daysScheduled.join(', ');
  } else {
    text += t('period.days.all');
  }

  text += '; ' + t('period.weekdays.label');
  if (periodicity.weekdaysPredefined) {
    text += predefines.weekdays.get(periodicity.weekdaysPredefined)?.name ?? '';
  } else if (periodicity.weekdaysScheduled?.length) {
    const weekdays = getWeekdayNames();
    text += periodicity.weekdaysScheduled
      .map((wd) => weekdays[Number(wd) - 1])
      .join(', ');
  } else {
    text += t('period.weekdays.all');
  }

  text += '; ' + t('period.months.label');
  if (periodicity.monthsPredefined) {
    text += predefines.months.get(periodicity.monthsPredefined)?.name ?? '';
  } else if (periodicity.monthsScheduled?.length) {
    const months = getMonthNames();
    text += periodicity.monthsScheduled
      .map((month) => months[Number(month) - 1])
      .join(', ');
  } else {
    text += t('period.months.all');
  }

  if (periodicity.bankingDays) {
    text += `; ${t('period.bankingDays.label')}`;
  }

  return text;
}

export function usePeriodPredefines(): PredefineClassifiers {
  const {
    PREDEFINED_MINUTES: { systemClassifierItems: minutes },
    PREDEFINED_HOURS: { systemClassifierItems: hours },
    PREDEFINED_DAYS: { systemClassifierItems: days },
    PREDEFINED_WEEKDAYS: { systemClassifierItems: weekdays },
    PREDEFINED_MONTHS: { systemClassifierItems: months },
  } = useSystemClassifiers(
    'PREDEFINED_MINUTES',
    'PREDEFINED_HOURS',
    'PREDEFINED_DAYS',
    'PREDEFINED_WEEKDAYS',
    'PREDEFINED_MONTHS'
  );

  return useMemo(
    () => ({
      minutes: codes(minutes),
      hours: codes(hours),
      days: codes(days),
      weekdays: codes(weekdays),
      months: codes(months),
    }),
    [days, hours, minutes, months, weekdays]
  );
}

export function usePeriodLoadingType() {
  const [periodLoadingTypeId, periodCount]: [number, number | null] = useWatch({
    name: ['execution.periodLoadingTypeId', 'execution.periodCount'],
  });
  const periodLoadingType = useSystemClassifierItemById(
    'PERIOD_LOADING_TYPE',
    periodLoadingTypeId
  )?.code;

  return { periodLoadingType, periodCount };
}

function codes(items: SystemClassifierItemDto[]) {
  return new Map(items.map((item) => [item.code, item]));
}

export function renderLoadingType(
  loadingType?: string,
  periodCount?: number | null
) {
  if (periodCount) {
    const options: { [key: string]: string | null } = {
      count: periodCount?.toString() ?? '',
    };

    return t('periodLoadingTypeId.count', options);
  }

  return loadingType ? t(`periodLoadingTypeId.${loadingType}`) : '';
}
